/**
 * Session 逾期訊息的交接。
 *
 * 問題
 * ----
 * `apiRequest` 收到 401 時的流程是：
 *     clearToken() → onUnauthorized() → router.replace("/login") → 然後才 throw
 *
 * `router.replace("/login")` 會把整個 `(dashboard)` 子樹**連同 ToastProvider
 * 一起卸載**。呼叫端 catch 到錯誤後就算顯示 toast，那個 toast 也隨即被卸載，
 * 撐不到預設的 3.5 秒。使用者只看到畫面突然跳走，不知道發生什麼事。
 *
 * 這是「錯誤被吞掉」的一種變形——**訊息確實產生了，但使用者來不及看見**。
 *
 * 解法
 * ----
 * 把「導頁」與「顯示訊息」變成同一件事的兩個必然步驟，而不是兩條互相賽跑的路徑：
 *   1. `onUnauthorized` 導頁前先寫一個一次性旗標到 sessionStorage
 *   2. 登入頁掛載時讀取，讀到就顯示訊息，**讀完立刻清除**
 *
 * 用 sessionStorage 而非 localStorage：這則訊息只對「這個分頁的這一次跳轉」有意義，
 * 不該在使用者明天開新分頁時又冒出來。
 *
 * 一次性（讀完即清）是關鍵：否則使用者手動重新整理登入頁還會再看到一次過期的訊息。
 *
 * 為什麼做成 useSyncExternalStore 的外部 store（2026-09-03）
 * -------------------------------------------------------
 * 登入頁原本是 `useEffect(() => { if (consume()) setErrorMessage(…) }, [])`，
 * 被 `react-hooks/set-state-in-effect` 判為 error。這條規則不是誤報：
 * 「掛載後才讀得到的瀏覽器狀態」在 React 裡有專用 API（useSyncExternalStore），
 * 它內建 server snapshot，不必在 effect 裡補一次 render。
 *
 * 這裡刻意**不**改成 `useState(consume)` 惰性初始值：
 *   - 登入頁會被 Next.js 在伺服器端預渲染，惰性初始值在 render 期間就執行，
 *     server 上沒有 `window.sessionStorage`，直接 ReferenceError；
 *   - 就算加 `typeof window` 防護躲掉例外，server 得到 false、瀏覽器首次
 *     （hydration）render 得到 true，兩邊 DOM 不一致 → hydration mismatch，
 *     React 會丟掉整棵子樹重繪。
 * `useSyncExternalStore` 的第三個參數就是為了這個情境存在的：hydration 用
 * server snapshot（永遠 false），掛載完成後 React 自己去比對 client snapshot
 * 並重繪，整個過程沒有任何一行我們自己寫的 setState。
 */

const KEY = "ccheng_session_expired";

/**
 * store 目前的值。`null` = 還沒從 sessionStorage 接手過。
 *
 * 這一格同時擔任 `getSnapshot` 的快取：`useSyncExternalStore` 要求 getSnapshot
 * 是可重入的（同一次 render 可能被呼叫多次，StrictMode 下 render 還會跑兩遍），
 * 若每次都直接去讀＋清 sessionStorage，第二次就會拿到 false，訊息會閃一下消失。
 */
let pending: boolean | null = null;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** 導向登入頁之前呼叫，讓登入頁知道「這次不是使用者自己來的」。 */
export function markSessionExpired(): void {
  try {
    window.sessionStorage.setItem(KEY, "1");
  } catch {
    // 無痕視窗／已封鎖站台資料：頂多少一則說明，導頁本身仍然會發生。
    // 這裡不能因為存不進去就中止導頁——token 已經失效，留在原頁只會一直失敗。
  }
  // 讓下一次 getSnapshot 重新接手：同一個分頁裡第二次逾時也要能再顯示一次，
  // 不能被上一次留下的快取值蓋掉。
  pending = null;
  notify();
}

/**
 * 訂閱（`useSyncExternalStore` 第一參數）。
 *
 * 這個 store 的值只會被 `markSessionExpired` / `acknowledgeSessionExpired`
 * 改動，兩者都是明確的事件，所以訂閱只是把 React 的重繪掛上去而已。
 * 必須是模組層的具名函式（identity 穩定），否則每次 render 都會重新訂閱。
 */
export function subscribeSessionExpired(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

/**
 * 瀏覽器端快照（`useSyncExternalStore` 第二參數）。
 * 第一次呼叫時把旗標從 sessionStorage 接手過來（**讀完即清**），之後回傳快取值。
 *
 * 「讀完即清」放在這裡而不是 effect 裡，是因為 sessionStorage 只是跨導頁的傳輸
 * 管道，值一旦進到這個模組就不該再留在儲存空間裡——留著的話使用者手動重新整理
 * 登入頁會再看到一次已經過期的訊息。
 */
export function getSessionExpiredSnapshot(): boolean {
  if (pending === null) {
    let hit = false;
    try {
      hit = window.sessionStorage.getItem(KEY) === "1";
      if (hit) window.sessionStorage.removeItem(KEY);
    } catch {
      // 無痕視窗／已封鎖站台資料：讀不到就當作沒有逾時訊息，登入頁照常顯示。
      hit = false;
    }
    pending = hit;
  }
  return pending;
}

/**
 * 伺服器端／hydration 快照（`useSyncExternalStore` 第三參數）。
 *
 * 伺服器沒有 sessionStorage，也不可能知道這個瀏覽器分頁發生過什麼，
 * 所以預渲染時一律當作「沒有逾時訊息」。
 */
export function getSessionExpiredServerSnapshot(): boolean {
  return false;
}

/**
 * 使用者已經看到訊息並開始重新登入 —— 把旗標收掉。
 *
 * 為什麼需要這一支：`pending` 是模組層變數，活得比登入頁元件久。少了這一步，
 * 「逾時 → 看到訊息 → 成功登入 → 之後按登出回到 /login」會因為 `pending`
 * 還是 true 而再顯示一次「登入已失效」，但那次其實是使用者自己登出的。
 * 收掉的時機放在送出表單（明確的使用者事件），不是 effect。
 */
export function acknowledgeSessionExpired(): void {
  if (pending === false) return;
  pending = false;
  notify();
}

/** 與 packages/api-client 的 401 文案一致，不另造第二種說法。 */
export const SESSION_EXPIRED_MESSAGE = "登入已失效，請重新登入";
